import React from 'react'
import PropTypes from 'prop-types'
import { UserPlusIcon } from '@heroicons/react/24/outline'

// Components
import { Button } from '../Button'
import { Modal } from '../Modal'
import { Select } from '../Select'

/**
 *
 * AssignLicenseModal
 *
 * Lets an EE admin assign, reassign or unassign a license from the exhibitor info
 * page. Unlike the exhibitor-facing licenses page, EE users aren't bound by the
 * event start date — onsite staff need to move a license when booth staff change.
 */
const AssignLicenseModal = ({
  assignee,
  license,
  loading,
  onSubmit,
  options,
  setAssignee,
  setSearchTerm,
  setShowModal,
  showModal,
}) => {
  const currentAssignee = license.assignee
  const willUnassign = !assignee && !!currentAssignee
  const unchanged = assignee ? assignee.id === currentAssignee?.id : !currentAssignee

  const submitLabel = () => {
    if (willUnassign) return 'Unassign'
    return currentAssignee ? 'Reassign' : 'Assign'
  }

  return (
    <Modal
      actions={[
        { type: 'cancel', label: 'Cancel', onClick: () => setShowModal(false) },
        {
          type: 'submit',
          label: submitLabel(),
          disabled: unchanged,
          onClick: () => onSubmit(assignee ? assignee.id : null),
        },
      ]}
      icon={<UserPlusIcon className="h-5 stroke-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col gap-4 sm:mt-5">
          <span className="text-left text-sm text-gray-600">
            {currentAssignee ? (
              <>
                License <span className="font-bold">{license.licenseId}</span> is assigned to{' '}
                <span className="font-bold">
                  {currentAssignee.user.firstName} {currentAssignee.user.lastName}
                </span>
                . Pick a different staff member to move it, or unassign it to free it up.
              </>
            ) : (
              <>
                Assign license <span className="font-bold">{license.licenseId}</span> to a member
                of this exhibitor&apos;s staff.
              </>
            )}
          </span>

          <Select
            className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
            disabled={loading}
            fullWidth
            hoverElement={
              currentAssignee ? (
                <Button
                  label="Unassign"
                  onClick={() => setAssignee(null)}
                  className="border-transparent bg-transparent py-0 leading-none hover:bg-transparent sm:py-0"
                />
              ) : undefined
            }
            id="assignee"
            label=""
            name="assignee"
            nunito
            onChange={setAssignee}
            options={options}
            placeholder="Search for a Staff Member"
            search
            setSearchTerm={setSearchTerm}
            style={{ flex: true, width: '100%' }}
            value={assignee}
          />
        </div>
      }
      loading={loading}
      onClose={() => setShowModal(false)}
      open={showModal}
      setOpen={setShowModal}
      title={currentAssignee ? 'Reassign License' : 'Assign License'}
    />
  )
}

AssignLicenseModal.propTypes = {
  assignee: PropTypes.object,
  license: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  setAssignee: PropTypes.func.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
}

export default AssignLicenseModal
